import React, { useState, useEffect } from 'react';
import Chart from 'chart.js/auto';
import ApexCharts from 'react-apexcharts';
import { useTranslation } from 'react-i18next';
import { getPerformanceResponses } from '../../../../services/performanceService';
import { getScheduledNotificationForObservationPeriod } from '../../../../services/notificationsService';
import { fetchQ20Data } from '../../../../services/surveyService';
import { format } from 'date-fns';
import {
    Box,
    Typography,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    IconButton,
    Tooltip,
    Paper
} from '@mui/material';
import {
    ArrowBack,
    CompareArrows,
    HourglassBottom as HourlyIcon,
    CalendarToday as WeeklyIcon
} from '@mui/icons-material';

const SurveyProgressBar = ({ userId, onReturn }) => {
    const { t } = useTranslation();
    const [scheduledNotifications, setScheduledNotifications] = useState([]);
    const [performanceData, setPerformanceData] = useState([]);
    const [q20Data, setQ20Data] = useState({ startTime: '', duration: 0 });
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [chartView, setChartView] = useState('hourly'); // 'hourly' or 'weekly'
    const [chartRef, setChartRef] = useState(null);
    const [apexChartOptions, setApexChartOptions] = useState({});
    const [notificationRangeText, setNotificationRangeText] = useState('');
    const [apexChartSeries, setApexChartSeries] = useState([]);

const generateHourlyChart = (canvasRef) => {
    if (!canvasRef || !scheduledNotifications || performanceData.length === 0) return;

    if (canvasRef.chartInstance) {
        canvasRef.chartInstance.destroy();
    }

    // Extract dates for the selected period
    const startDate = new Date(selectedPeriod.startDate);
    const endDate = new Date(selectedPeriod.endDate);

    // Filter performance data for the selected period
    const filteredPerformanceData = performanceData.filter(entry => {
        const entryDate = new Date(entry.timestamp);
        return entryDate >= startDate && entryDate <= endDate;
    });

    // Filter notification times to get the notification time range (for display purposes)
    const notificationTimes = scheduledNotifications
        .find(range => range.startDate === selectedPeriod.startDate)?.notifications
        .map(notification => {
            const notificationDate = new Date(notification.notificationTime);
            return notificationDate.getHours();
        }) || [];

    // Get the notification time range
    const earliestNotificationHour = notificationTimes.length > 0 ? Math.min(...notificationTimes) : null;
    const latestNotificationHour = notificationTimes.length > 0 ? Math.max(...notificationTimes) : null;
    const notificationTimeRange = notificationTimes.length > 0 
        ? `Notification time range: ${earliestNotificationHour}:00 - ${latestNotificationHour}:00`
        : 'No notification times available';

    // Get all hours that have performance data
    const allHours = [...new Set(filteredPerformanceData.map(entry => 
        new Date(entry.timestamp).getHours()
    ))].sort((a, b) => a - b);

    // Generate time slots for all hours with data
    const timeSlots = allHours.map(hour => `${hour}:00`);

    // Group performance scores by hour
    const hourlyScores = {};
    
    filteredPerformanceData.forEach(entry => {
        const entryHour = new Date(entry.timestamp).getHours();
        if (!hourlyScores[entryHour]) {
            hourlyScores[entryHour] = [];
        }
        hourlyScores[entryHour].push(entry.score);
    });

    // Calculate average scores for each time slot
    const averageScores = timeSlots.map(slot => {
        const hour = parseInt(slot.split(':')[0], 10);
        const scores = hourlyScores[hour] || [];
        
        return scores.length > 0
            ? scores.reduce((sum, score) => sum + score, 0) / scores.length
            : 0;
    });

    // Color mapping function with adjusted colors for clarity
    const getColorForValue = (value, hour) => {
        // Check if this hour is within notification time range (for visual distinction)
        const isNotificationHour = hour >= earliestNotificationHour && hour <= latestNotificationHour;
        
        if (value === 0) return '#E0E0E0'; // Light gray for no data
        
        // Adjust colors based on score range
        if (value >= 1 && value < 3) {
            return isNotificationHour ? '#ADD8E6' : '#D6EBFF'; // Lighter blue if not notification hour
        }
        if (value >= 3 && value < 5) {
            return isNotificationHour ? '#0000FF' : '#6666FF'; // Lighter blue if not notification hour
        }
        if (value >= 5 && value < 7) {
            return isNotificationHour ? '#00008B' : '#4444AA'; // Lighter dark blue if not notification hour
        }
        if (value >= 7 && value <= 10) {
            return isNotificationHour ? '#008000' : '#66AA66'; // Lighter green if not notification hour
        }
        
        return '#E0E0E0'; // Light gray default
    };

    // Extract hours from time slots for color mapping
    const hours = timeSlots.map(slot => parseInt(slot.split(':')[0], 10));

    // Create chart context
    const ctx = canvasRef.getContext('2d');

    // Create new Chart.js instance
    const newChartInstance = new Chart(ctx, {
        type: 'bar',
        data: {
            labels: timeSlots,
            datasets: [{
                label: 'Average Performance Score',
                data: averageScores,
                backgroundColor: averageScores.map((score, index) => 
                    getColorForValue(score, hours[index])
                ),
            }],
        },
        options: {
            responsive: true,
            plugins: {
                tooltip: {
                    callbacks: {
                        label: (tooltipItem) => {
                            const hour = parseInt(tooltipItem.label.split(':')[0], 10);
                            const isNotificationHour = hour >= earliestNotificationHour && hour <= latestNotificationHour;
                            return [
                                `Average Score: ${tooltipItem.raw.toFixed(2)}`,
                                isNotificationHour ? 'Within notification time range' : 'Outside notification time range'
                            ];
                        },
                    },
                },
                // Add annotation plugin to show notification time range
                annotation: {
                    annotations: {
                        box1: {
                            type: 'box',
                            xMin: timeSlots.findIndex(slot => parseInt(slot.split(':')[0], 10) === earliestNotificationHour) - 0.5,
                            xMax: timeSlots.findIndex(slot => parseInt(slot.split(':')[0], 10) === latestNotificationHour) + 0.5,
                            backgroundColor: 'rgba(255, 255, 0, 0.1)',
                            borderColor: 'rgba(255, 255, 0, 0.5)',
                            borderWidth: 1,
                            label: {
                                display: true,
                                content: 'Notification Range',
                                position: 'start',
                                backgroundColor: 'rgba(255, 255, 0, 0.7)',
                                color: '#000',
                                font: {
                                    size: 10
                                }
                            }
                        }
                    }
                }
            },
            scales: {
                x: {
                    title: {
                        display: true,
                        text: 'Time Slots',
                    },
                },
                y: {
                    title: {
                        display: true,
                        text: 'Average Score',
                    },
                    suggestedMin: 0,
                    suggestedMax: 10,
                },
            },
        },
    });


    canvasRef.chartInstance = newChartInstance;
    setNotificationRangeText(notificationTimeRange);
};



    const generateWeeklyChart = () => {
        // Existing weekly chart generation logic from ApexCharts version
        const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
        const [startHour] = q20Data.startTime.split(':').map(Number);
        const duration = q20Data.duration;
        const timeSlots = Array.from({ length: duration }, (_, i) => `${startHour + i}:00`);

        const startDate = new Date(selectedPeriod.startDate);
        const endDate = new Date(selectedPeriod.endDate);

        const scoresMatrix = Array.from({ length: weekdays.length }, () =>
            Array(timeSlots.length).fill(null)
        );

        performanceData.forEach(entry => {
            const entryDate = new Date(entry.timestamp);
            const weekdayIndex = entryDate.getDay() - 1;
            const hour = entryDate.getHours();
            const timeSlotIndex = hour - startHour;

            if (weekdayIndex >= 0 && weekdayIndex < 5 &&
                timeSlotIndex >= 0 && timeSlotIndex < duration &&
                entryDate >= startDate && entryDate <= endDate) {

                if (scoresMatrix[weekdayIndex][timeSlotIndex] === null) {
                    scoresMatrix[weekdayIndex][timeSlotIndex] = [];
                }
                scoresMatrix[weekdayIndex][timeSlotIndex].push(entry.score);
            }
        });

        const series = timeSlots.map((timeSlot, timeSlotIndex) => ({
            name: timeSlot,
            data: weekdays.map((_, weekdayIndex) => {
                const scores = scoresMatrix[weekdayIndex][timeSlotIndex];
                return scores && scores.length
                    ? Math.floor(scores.reduce((a, b) => a + b, 0) / scores.length)
                    : null;
            })
        }));

        setApexChartSeries(series);
        setApexChartOptions({
            chart: { type: 'bar', stacked: true, toolbar: { show: true } },
            xaxis: {
                categories: weekdays,
                title: { text: 'Weekdays' }
            },
            yaxis: {
                title: { text: 'Scores' },
                labels: {
                    formatter: (value) => (value === null ? 'No Data' : value)
                }
            },
            tooltip: {
                y: {
                    formatter: (value) => {
                        if (value === null) return 'No Data';
                        if (value >= 1 && value <= 2) return `Score: ${value} (1-2)`;
                        if (value >= 3 && value <= 4) return `Score: ${value} (3-4)`;
                        if (value >= 5 && value <= 6) return `Score: ${value} (5-6)`;
                        if (value >= 7 && value <= 10) return `Score: ${value} (7-10)`;
                        return 'Score: No Data';
                    }
                }
            },
            plotOptions: {
                bar: {
                    horizontal: false,
                    dataLabels: { position: 'top' },
                    distributed: false
                }
            },
            fill: { opacity: 1 },
            legend: { show: true }
        });
    };

    // Data fetching effect
    useEffect(() => {
        const fetchData = async () => {
            // Don't make API calls if userId is not provided or invalid
            if (!userId || userId === 'undefined') {
                console.warn('SurveyProgressBar: userId is not defined, skipping data fetch');
                return;
            }

            try {
                const [notificationsResponse, performanceResponses, surveyData] = await Promise.all([
                    getScheduledNotificationForObservationPeriod(userId),
                    getPerformanceResponses(userId),
                    fetchQ20Data(userId)
                ]);

                const { dateRanges } = notificationsResponse;
                setScheduledNotifications(dateRanges);

                if (dateRanges.length > 0) {
                    setSelectedPeriod({
                        startDate: dateRanges[0].startDate,
                        endDate: dateRanges[0].endDate,
                        label: `Period 1: ${format(new Date(dateRanges[0].startDate), 'yyyy-MM-dd')}`
                    });
                }

                setPerformanceData(performanceResponses.data || []);
                setQ20Data(surveyData);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
    }, [userId]);

    const toggleChartView = () => {
        setChartView(prevView => prevView === 'hourly' ? 'weekly' : 'hourly');
    };


    // Chart generation effects
    useEffect(() => {
        if (chartView === 'weekly') {
            generateWeeklyChart();
        }
    }, [selectedPeriod, q20Data, performanceData, chartView]);

    // Render check
    if (!selectedPeriod || !q20Data.startTime || !q20Data.duration || !performanceData.length) {
        return <Typography>Loading chart...</Typography>;
    }

    return (
        <Box sx={{ padding: 4 }}>
            <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                <IconButton onClick={onReturn}>
                    <ArrowBack />
                </IconButton>
                <Typography variant="h6" sx={{ ml: 2, flexGrow: 1 }}>
                    Performance Question Progress
                </Typography>
                <Tooltip title="Switch Chart View">
                    <IconButton onClick={toggleChartView}>
                        <CompareArrows />
                    </IconButton>
                </Tooltip>
            </Box>

            <Paper
                elevation={3}
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    mb: 2
                }}
            >
                <Box display="flex" alignItems="center">
                    {chartView === 'hourly' ? <HourlyIcon sx={{ mr: 1 }} /> : <WeeklyIcon sx={{ mr: 1 }} />}
                    <Typography>
                        {chartView === 'hourly' ? 'Hourly Performance View' : 'Weekly Performance View'}
                    </Typography>
                </Box>
                <Box>
                    <Typography variant="body2" color="textSecondary">
                        {chartView === 'hourly'
                            ? 'Shows average performance scores by hour'
                            : 'Shows performance scores across weekdays and time slots'}
                    </Typography>
                </Box>
            </Paper>
            



            <FormControl fullWidth sx={{ mb: 4 }}>
                <InputLabel>{t('surveyProgress.selectObservationPeriod')}</InputLabel>
                <Select
                    value={selectedPeriod?.startDate || ''}
                    onChange={(event) => {
                        const selected = scheduledNotifications.find(
                            range => range.startDate === event.target.value
                        );
                        setSelectedPeriod({
                            startDate: selected.startDate,
                            endDate: selected.endDate,
                            label: `Period ${scheduledNotifications.indexOf(selected) + 1}: ${format(new Date(selected.startDate), 'yyyy-MM-dd')}`
                        });
                    }}
                >
                    {scheduledNotifications.map((range, index) => (
                        <MenuItem key={index} value={range.startDate}>
                            {`Period ${index + 1}: ${format(new Date(range.startDate), 'yyyy-MM-dd')} - ${format(new Date(range.endDate), 'yyyy-MM-dd')}`}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
                            
            <Typography 
                variant="caption" 
                sx={{ 
                    display: 'block', 
                    textAlign: 'center', 
                    mt: 1, 
                    fontStyle: 'italic',
                    color: 'text.secondary'
                }}
            >
                {notificationRangeText}
            </Typography>

            {chartView === 'hourly' ? (
                <Box>
                    <Box sx={{ mb: 4 }}>
                        <canvas
                            ref={(ref) => {
                                if (ref) {
                                    setChartRef(ref);
                                    generateHourlyChart(ref);
                                }
                            }}
                        />
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            mb: 2,
                            flexWrap: 'wrap'
                        }}
                    >
                        {[
                            { color: '#ADD8E6', range: '1-2', label: 'Very Low' },
                            { color: '#0000FF', range: '3-4', label: 'Low' },
                            { color: '#00008B', range: '5-6', label: 'Moderate' },
                            { color: '#008000', range: '7-10', label: 'High' },
                            { color: '#808080', range: 'N/A', label: 'No Data' }
                        ].map((item) => (
                            <Box
                                key={item.range}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mr: 2,
                                    mb: 1
                                }}
                            >
                                <Box
                                    sx={{
                                        backgroundColor: item.color,
                                        width: 20,
                                        height: 20,
                                        mr: 1
                                    }}
                                />
                                <Typography sx={{ fontSize: '12px' }}>
                                    {`${item.range}: ${item.label}`}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            ) : (
                // Conditional rendering for ApexCharts
                apexChartOptions && apexChartSeries.length > 0 ? (
                    <Box sx={{ mb: 4 }}>
                        <ApexCharts
                            options={apexChartOptions}
                            series={apexChartSeries}
                            type="bar"
                            height={400}
                        />
                    </Box>
                ) : (
                    <Typography>Loading chart...</Typography>
                )
            )}
        </Box>
    );
};

export default SurveyProgressBar;